import { useState, useRef } from "react";
import NewsImageWithFallback from "../news/NewsImageWithFallback";

const LANGUAGE_LABELS = {
  en: "English", de: "Deutsch", fr: "Français", es: "Español", it: "Italiano",
  pt: "Português", nl: "Nederlands", pl: "Polski", hr: "Hrvatski",
};

const TopNewsCarousel = ({
  isLoadingTopNews,
  topNewsError,
  topNews,
  fetchTopNews,
  handleNewsClick,
  apiLanguage,
  changeLanguage,
}) => {
  const lang = (apiLanguage || "en").toLowerCase();
  const localeName = LANGUAGE_LABELS[lang] || lang.toUpperCase();
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollContainerRef = useRef(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [mouseStartX, setMouseStartX] = useState(0);
  const [scrollStartX, setScrollStartX] = useState(0);
  
  // Fixed dimensions for consistent layout. The image box keeps a stable
  // height so the cards stay aligned in the row, and the image inside is shown
  // WHOLE (NewsImageWithFallback defaults to object-contain) on a neutral
  // ground: a portrait keeps its head, a wide photo shows fully, nothing is
  // cropped. The box is a touch taller than before so a contained portrait
  // still reads clearly; card height grows to match. Neutral bg behind any
  // letterbox.
  const cardClasses = "w-[280px] sm:w-[320px] flex-shrink-0 h-[380px] sm:h-[400px]";
  const imageClasses = "relative w-full h-[210px] sm:h-[230px] overflow-hidden rounded-md bg-[#F1F3F5]";

  const handleScrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: -400, behavior: "smooth" });
      setScrollPosition(Math.max(0, scrollPosition - 400));
    }
  };

  const handleScrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: 400, behavior: "smooth" });
      setScrollPosition(scrollPosition + 400);
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsMouseDown(true);
    setMouseStartX(e.clientX);
    setScrollStartX(scrollContainerRef.current.scrollLeft);
    e.currentTarget.style.cursor = "grabbing";
    e.currentTarget.style.userSelect = "none";
  };

  const handleMouseMove = (e) => {
    if (!isMouseDown) return;
    e.preventDefault();
    const deltaX = e.clientX - mouseStartX;
    scrollContainerRef.current.scrollLeft = scrollStartX - deltaX;
  };

  const handleMouseUp = (e) => {
    setIsMouseDown(false);
    e.currentTarget.style.cursor = "grab";
    e.currentTarget.style.removeProperty("user-select");
  };

  const handleMouseLeave = (e) => {
    setIsMouseDown(false);
    e.currentTarget.style.cursor = "grab";
    e.currentTarget.style.removeProperty("user-select");
  };

  return (
    <>
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      <div className="py-4 px-4 md:px-12 border-b border-[#E5E6E8]">
        <h1 className="text-base md:text-xl font-semibold">Top news</h1>
      </div>

      <div className="relative mt-4 px-4 md:px-12">
        {/* Left Arrow */}
        <button
          onClick={handleScrollLeft}
          className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-2 shadow-md hover:bg-gray-50 transition-colors"
          style={{ display: scrollPosition <= 0 ? "none" : "block" }}
        >
          <svg
            className="w-5 h-5 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {/* Right Arrow */}
        <button
          onClick={handleScrollRight}
          className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-2 shadow-md hover:bg-gray-50 transition-colors"
        >
          <svg
            className="w-5 h-5 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        {/* Scrollable Container */}
        <div
          ref={scrollContainerRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide cursor-grab"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {isLoadingTopNews ? (
            // Loading state - show skeleton loaders
            Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className={`bg-[#F3F4F7] p-4 rounded-lg flex flex-col gap-3 select-none ${cardClasses}`}
              >
                <div className={imageClasses}>
                  <div className="absolute inset-0 bg-gray-200 animate-pulse" />
                </div>
                <div className="w-24 h-4 bg-gray-200 animate-pulse rounded"></div>
                <div className="w-full h-6 bg-gray-200 animate-pulse rounded"></div>
              </div>
            ))
          ) : topNewsError ? (
            // Error state
            <div
              className={`bg-[#F3F4F7] p-4 rounded-lg flex flex-col justify-center items-center gap-3 ${cardClasses}`}
            >
              <div className="text-red-500 text-center">
                <svg
                  className="w-12 h-12 mx-auto mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="font-medium">Failed to load news</p>
                <button
                  onClick={fetchTopNews}
                  className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Try again
                </button>
              </div>
            </div>
          ) : topNews.length === 0 ? (
            // Empty state — when a non-English locale has no football news,
            // we offer a one-click switch to English instead of leaving the
            // user with a dead carousel.
            <div
              className={`bg-[#F3F4F7] p-4 rounded-lg flex flex-col justify-center items-center text-center gap-3 ${cardClasses}`}
            >
              <svg
                className="w-16 h-16 text-gray-400 mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2"
                />
              </svg>
              {lang !== "en" ? (
                <>
                  <p className="text-gray-700 font-medium">
                    Currently no football news in {localeName}.
                  </p>
                  {typeof changeLanguage === "function" && (
                    <button
                      type="button"
                      onClick={() => changeLanguage("en")}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg bg-[#4A90D9] hover:bg-[#2F78C4] text-white text-sm font-semibold transition-colors"
                    >
                      Switch to English
                    </button>
                  )}
                </>
              ) : (
                <p className="text-gray-500">No news available</p>
              )}
            </div>
          ) : (
            // News items — entire card is a link, plus an explicit
            // "Read article" button at the bottom for discoverability.
            topNews.map((news, index) => (
              <div
                key={news.id || index}
                className={`bg-[#F3F4F7] p-4 rounded-lg flex flex-col gap-3 select-none cursor-pointer hover:bg-[#EBF5FF] transition-colors ${cardClasses}`}
                draggable={false}
                onClick={() => handleNewsClick(news)}
                role="link"
                tabIndex={0}
                aria-label={news.title || "Read article"}
                onKeyDown={(e) => { if (e.key === "Enter") handleNewsClick(news); }}
              >
                <NewsImageWithFallback
                  imageUrl={news.image}
                  alt={news.title || ""}
                  sourceName={news.source}
                  className={imageClasses}
                  rounded="rounded-md"
                />

                <div className="text-[#374DF5] text-xs font-medium truncate">
                  {news.source}
                </div>

                <div className="text-[#100F0F] font-bold text-base line-clamp-3 flex-1">
                  {news.title}
                </div>

                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleNewsClick(news); }}
                  className="mt-auto inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-[#4A90D9] hover:bg-[#2F78C4] text-white text-xs font-semibold transition-colors w-fit"
                  aria-label={`Read article: ${news.title || ""}`}
                >
                  Read article
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default TopNewsCarousel;
